/**
 * Door access.
 *
 * The club's side of a system it does not own, so the screen is organised
 * around the two questions the office actually asks: "why can this person get
 * in?" and "why can't they?". Everything else — doors, groups, the event log —
 * exists to answer those.
 *
 * The status panel at the top is deliberately blunt about what is and is not
 * connected. An access-control integration that silently stopped syncing looks
 * exactly like one that is working, right up until somebody's fob opens a door
 * it should not.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACCESS_CREDENTIAL_STATE, ROLES, shortDate,
  type AccessCredential, type AccessDoor, type AccessEvent, type AccessGroup,
  type AccessSyncReport, type Member,
} from '@lcc/shared';
import { api } from '../lib/api';
import { useDebounced } from '../lib/hooks';
import { PageHeader } from '../App';
import {
  Card, CheckField, Empty, ErrorNote, Loading, Modal, Pill, SelectField, Stat, Tabs,
  TextField, errorMessage, useToast,
} from '../components/ui';

interface AccessStatus {
  provider: 'manual' | 'kisi' | 'brivo';
  remote: boolean;
  configured: boolean;
  probe: { ok: boolean; message: string; doorsFound: number; groupsFound: number };
  counts: { doors: number; groups: number; credentials: number; unmapped: number };
  lastEventAt: string | null;
  lastSyncAt: string | null;
  lastSyncOk: boolean | null;
  webhookConfigured: boolean;
}

type Tab = 'people' | 'groups' | 'doors' | 'events';

export function AccessPage() {
  const [tab, setTab] = useState<Tab>('people');
  const toast = useToast();
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: ['access', 'status'],
    queryFn: () => api.get<{ data: AccessStatus }>('/access/status').then((r) => r.data),
    refetchInterval: 120_000,
  });

  const sync = useMutation({
    mutationFn: () => api.post<{ data: AccessSyncReport }>('/access/sync', {}).then((r) => r.data),
    onSuccess: (report) => {
      if (report.errors.length === 0) {
        toast.success(`Synced ${report.membersConsidered} member(s).`);
      } else {
        toast.error(
          `${report.membersConsidered} considered, ${report.errors.length} could not be pushed. `
          + report.errors[0]!.message,
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['access'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <>
      <PageHeader
        title="Door access"
        sub="Who gets in, with what, and through which door"
        actions={
          <button
            className="btn btn--primary"
            type="button"
            disabled={sync.isPending}
            onClick={() => sync.mutate()}
          >
            {sync.isPending ? 'Syncing…' : 'Sync now'}
          </button>
        }
      />
      <div className="content stack">
        {status.data && <StatusPanel status={status.data} />}

        <Tabs
          tabs={[
            { id: 'people', label: 'People' },
            { id: 'groups', label: 'Groups', count: status.data?.counts.groups },
            { id: 'doors', label: 'Doors', count: status.data?.counts.doors },
            { id: 'events', label: 'Door log' },
          ] as const}
          active={tab}
          onChange={setTab}
        />

        {tab === 'people' && <People />}
        {tab === 'groups' && <Groups />}
        {tab === 'doors' && <Doors />}
        {tab === 'events' && <Events />}
      </div>
    </>
  );
}

/* --------------------------------------------------------------- status -- */

function StatusPanel({ status }: { status: AccessStatus }) {
  const warnings: string[] = [];
  if (status.remote && !status.probe.ok) warnings.push(status.probe.message);
  if (status.remote && !status.webhookConfigured) {
    warnings.push(
      'No webhook secret is set, so door events only arrive when somebody presses '
      + 'Sync. Set ACCESS_WEBHOOK_SECRET to get them as they happen.',
    );
  }
  if (status.counts.unmapped > 0 && status.remote) {
    warnings.push(
      `${status.counts.unmapped} group(s) are not linked to a group at the provider, `
      + 'so nobody in them will actually be let in.',
    );
  }

  return (
    <>
      <div className="grid grid--4">
        <Stat
          label="Provider"
          value={status.provider === 'manual' ? 'The club\'s own fobs' : status.provider}
          note={status.remote
            ? (status.probe.ok ? 'Connected' : 'Not reachable')
            : 'Nothing is pushed anywhere'}
          alert={status.remote && !status.probe.ok}
        />
        <Stat label="Active fobs" value={status.counts.credentials} />
        <Stat label="Doors" value={status.counts.doors} />
        <Stat
          label="Last door event"
          value={status.lastEventAt ? shortDate(status.lastEventAt) : 'None yet'}
        />
      </div>

      {warnings.map((w) => (
        <div className="banner banner--error" key={w} role="alert"><strong>{w}</strong></div>
      ))}

      {!status.remote && (
        <div className="banner">
          No access-control provider is configured, so this is the club&apos;s own
          record: fobs, who may open what, and a door log the office fills in.
          Set <code>ACCESS_PROVIDER</code> to <code>kisi</code> or <code>brivo</code> to
          push it to real hardware.
        </div>
      )}
    </>
  );
}

/* --------------------------------------------------------------- people -- */

interface Profile {
  memberId: string;
  memberName: string;
  providerUserRef: string | null;
  syncedAt: string | null;
  syncError: string | null;
  credentials: AccessCredential[];
  groups: Array<{
    groupId: string; groupName: string; isManual: boolean;
    membershipOk: boolean; waiversOk: boolean; effective: boolean;
    doors: Array<{ doorId: string; doorName: string }>;
  }>;
  doors: Array<{ doorId: string; doorName: string }>;
  recentEvents: AccessEvent[];
}

function People() {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const search = useDebounced(q);

  const members = useQuery({
    queryKey: ['members', 'search', search],
    queryFn: () => api.get<{ data: Member[] }>(
      `/members?q=${encodeURIComponent(search)}&perPage=10`,
    ).then((r) => r.data),
    enabled: search.trim().length >= 2,
  });

  const credentials = useQuery({
    queryKey: ['access', 'credentials', search],
    queryFn: () => api.get<{ data: AccessCredential[] }>(
      `/access/credentials?q=${encodeURIComponent(search)}`,
    ).then((r) => r.data),
    enabled: search.trim().length >= 2,
  });

  return (
    <div className="grid grid--2">
      <Card title="Find somebody, or a fob">
        <TextField
          label="Name, or the number on a fob"
          value={q}
          onChange={setQ}
          placeholder="Tremblay, or 0012 3456"
          hint="Searching by number is how a fob found in the car park gets home."
        />
        {members.data?.map((m) => (
          <button
            key={m.id}
            type="button"
            className="btn btn--sm btn--block"
            style={{ marginBottom: '.35rem', textAlign: 'left' }}
            onClick={() => setPicked(m.id)}
          >
            {m.fullName}
          </button>
        ))}
        {credentials.data && credentials.data.length > 0 && (
          <>
            <h3 className="small" style={{ marginTop: '1rem' }}>Matching fobs</h3>
            <table className="table">
              <tbody>
                {credentials.data.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <button
                        type="button"
                        className="btn btn--sm"
                        onClick={() => setPicked(c.memberId)}
                      >
                        {c.memberName}
                      </button>
                    </td>
                    <td className="mono">{c.cardNumber}</td>
                    <td><Pill tone={c.state === 'active' ? 'good' : 'bad'}>{c.state}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Card>

      {picked ? <ProfileCard memberId={picked} /> : (
        <Card title="Nobody selected">
          <p className="small">
            Pick a member to see what they hold, what it opens, and why.
          </p>
        </Card>
      )}
    </div>
  );
}

function ProfileCard({ memberId }: { memberId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [enrolling, setEnrolling] = useState(false);

  const profile = useQuery({
    queryKey: ['access', 'profile', memberId],
    queryFn: () => api.get<{ data: Profile }>(`/access/members/${memberId}`).then((r) => r.data),
  });

  const setState = useMutation({
    mutationFn: ({ id, state }: { id: string; state: string }) =>
      api.patch(`/access/credentials/${id}/state`, {
        state,
        reason: state === 'lost' ? 'Reported lost at the office' : null,
      }),
    onSuccess: () => {
      toast.success('Updated.');
      void queryClient.invalidateQueries({ queryKey: ['access'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (profile.isLoading) return <Loading what="Reading their access" />;
  if (profile.error) return <ErrorNote error={profile.error} />;
  const p = profile.data!;

  return (
    <>
      <Card
        title={p.memberName}
        actions={<button className="btn btn--sm" type="button" onClick={() => setEnrolling(true)}>
          Enrol a fob
        </button>}
      >
        {p.syncError && (
          <div className="banner banner--error"><strong>{p.syncError}</strong></div>
        )}

        <h3 className="small">Can open right now</h3>
        {p.doors.length
          ? <p>{p.doors.map((d) => d.doorName).join(', ')}</p>
          : <p className="small">Nothing. See the groups below for why.</p>}

        <h3 className="small" style={{ marginTop: '1rem' }}>Fobs and cards</h3>
        {p.credentials.length === 0 && <p className="small">None enrolled.</p>}
        {p.credentials.map((c) => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.35rem' }}>
            <span>{c.label ?? c.kind}</span>
            <span className="mono small">{c.cardNumber}</span>
            <Pill tone={c.state === 'active' ? 'good' : c.state === 'pending' ? 'warn' : 'bad'}>
              {c.state}
            </Pill>
            <div className="spacer" />
            <SelectField
              label=""
              value={c.state}
              onChange={(state) => setState.mutate({ id: c.id, state })}
              options={ACCESS_CREDENTIAL_STATE.map((s) => ({ value: s, label: s }))}
            />
          </div>
        ))}

        <h3 className="small" style={{ marginTop: '1rem' }}>Why</h3>
        <table className="table">
          <tbody>
            {p.groups.map((g) => (
              <tr key={g.groupId}>
                <td>{g.groupName}</td>
                <td>{g.isManual ? 'added by hand' : 'by rule'}</td>
                <td>
                  {g.effective
                    ? <Pill tone="good">in force</Pill>
                    : (
                      <Pill tone="bad">
                        {!g.membershipOk ? 'membership' : 'waivers'}
                      </Pill>
                    )}
                </td>
              </tr>
            ))}
            {p.groups.length === 0 && (
              <tr><td colSpan={3} className="small">In no access group.</td></tr>
            )}
          </tbody>
        </table>

        <h3 className="small" style={{ marginTop: '1rem' }}>Recent doors</h3>
        {p.recentEvents.length === 0 && <p className="small">No door events recorded.</p>}
        {p.recentEvents.slice(0, 8).map((e) => (
          <div key={e.id} className="small">
            {shortDate(e.occurredAt)} · {e.doorName ?? 'Unknown door'} ·{' '}
            {e.decision === 'granted' ? 'opened' : e.decision}
          </div>
        ))}
      </Card>

      {enrolling && (
        <EnrolFob
          memberId={memberId}
          onClose={() => setEnrolling(false)}
          onDone={() => {
            setEnrolling(false);
            void queryClient.invalidateQueries({ queryKey: ['access'] });
          }}
        />
      )}
    </>
  );
}

function EnrolFob(
  { memberId, onClose, onDone }: { memberId: string; onClose: () => void; onDone: () => void },
) {
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [facilityCode, setFacilityCode] = useState('');

  const create = useMutation({
    mutationFn: () => api.post('/access/credentials', {
      memberId, kind: 'fob', label: label || null,
      cardNumber, facilityCode: facilityCode || null,
    }),
    onSuccess: () => { toast.success('Fob enrolled.'); onDone(); },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Modal
      title="Enrol a fob"
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn btn--primary"
            type="button"
            disabled={!cardNumber.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Enrol
          </button>
        </>
      }
    >
      <TextField label="What to call it" value={label} onChange={setLabel} placeholder="Blue fob" />
      <TextField
        label="Number on the fob"
        value={cardNumber}
        onChange={setCardNumber}
        placeholder="0012 3456"
        hint="Spaces and dashes are fine — they are stripped out."
      />
      <TextField
        label="Facility code"
        value={facilityCode}
        onChange={setFacilityCode}
        hint="Only if the club's cards carry one. Leave blank otherwise."
      />
    </Modal>
  );
}

/* --------------------------------------------------------------- groups -- */

function Groups() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<AccessGroup | 'new' | null>(null);

  const groups = useQuery({
    queryKey: ['access', 'groups'],
    queryFn: () => api.get<{ data: AccessGroup[] }>('/access/groups').then((r) => r.data),
  });
  const doors = useQuery({
    queryKey: ['access', 'doors'],
    queryFn: () => api.get<{ data: AccessDoor[] }>('/access/doors').then((r) => r.data),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/access/groups/${id}`),
    onSuccess: () => {
      toast.success('Group deleted.');
      void queryClient.invalidateQueries({ queryKey: ['access'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (groups.isLoading) return <Loading what="Reading the groups" />;
  if (groups.error) return <ErrorNote error={groups.error} />;

  return (
    <>
      <Card
        title="Access groups"
        actions={<button className="btn btn--sm" type="button" onClick={() => setEditing('new')}>
          New group
        </button>}
        flush
      >
        {groups.data?.length
          ? (
            <table className="table">
              <thead>
                <tr><th>Group</th><th>Who is in it</th><th>Doors</th><th>People</th><th /></tr>
              </thead>
              <tbody>
                {groups.data.map((g) => (
                  <tr key={g.id}>
                    <td>
                      {g.name}
                      {!g.providerRef && <Pill tone="warn">not linked</Pill>}
                    </td>
                    <td className="small">
                      {[
                        g.autoAllMembers && 'every paid-up member',
                        g.autoRoles.length > 0 && g.autoRoles.join(', '),
                        g.requiresWaivers && 'waivers must be signed',
                      ].filter(Boolean).join(' · ') || 'hand-picked only'}
                    </td>
                    <td className="small">
                      {g.doors.map((d) => d.doorName).join(', ') || '—'}
                    </td>
                    <td>{g.memberCount}</td>
                    <td>
                      <button className="btn btn--sm" type="button" onClick={() => setEditing(g)}>
                        Edit
                      </button>{' '}
                      <button
                        className="btn btn--sm"
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Delete ${g.name}? Anyone who gets in only `
                            + 'through this group will stop getting in.')) remove.mutate(g.id);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
          : (
            <div className="card__body">
              <Empty title="No access groups yet">
                A group is a reason to be let in — &ldquo;members&rdquo;, &ldquo;ice
                crew&rdquo;, &ldquo;bar staff&rdquo; — and the doors that reason opens.
              </Empty>
            </div>
          )}
      </Card>

      {editing && (
        <GroupEditor
          group={editing === 'new' ? null : editing}
          doors={doors.data ?? []}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['access'] });
          }}
        />
      )}
    </>
  );
}

function GroupEditor(
  { group, doors, onClose, onDone }:
  { group: AccessGroup | null; doors: AccessDoor[]; onClose: () => void; onDone: () => void },
) {
  const toast = useToast();
  const [name, setName] = useState(group?.name ?? '');
  const [slug, setSlug] = useState(group?.slug ?? '');
  const [providerRef, setProviderRef] = useState(group?.providerRef ?? '');
  const [autoAll, setAutoAll] = useState(group?.autoAllMembers ?? false);
  const [autoRoles, setAutoRoles] = useState<string[]>(group?.autoRoles ?? []);
  const [requiresMembership, setRequiresMembership] = useState(group?.requiresMembership ?? true);
  const [requiresWaivers, setRequiresWaivers] = useState(group?.requiresWaivers ?? false);
  const [doorIds, setDoorIds] = useState<string[]>(group?.doors.map((d) => d.doorId) ?? []);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        slug: slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        providerRef: providerRef || null,
        autoAllMembers: autoAll,
        autoRoles,
        requiresMembership,
        requiresWaivers,
        doors: doorIds.map((doorId) => ({
          doorId, daysOfWeek: null, startsAt: null, endsAt: null,
        })),
      };
      return group
        ? api.patch(`/access/groups/${group.id}`, body)
        : api.post('/access/groups', body);
    },
    onSuccess: () => { toast.success('Saved.'); onDone(); },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Modal
      title={group ? `Edit ${group.name}` : 'New access group'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn btn--primary"
            type="button"
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </button>
        </>
      }
    >
      <TextField label="Name" value={name} onChange={setName} placeholder="Ice crew" />
      <TextField
        label="Short name"
        value={slug}
        onChange={setSlug}
        hint="Lower case and hyphens. Left blank, it is made from the name."
      />
      <TextField
        label="The provider's group id"
        value={providerRef}
        onChange={setProviderRef}
        hint="Without this, nobody in this group is actually pushed to the hardware."
      />

      <h3 className="small" style={{ marginTop: '1rem' }}>Who is in it</h3>
      <CheckField
        label="Every paid-up member this season"
        checked={autoAll}
        onChange={setAutoAll}
      />
      <div className="field">
        <label>Anyone holding these roles</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
          {ROLES.map((r) => (
            <label key={r} className="check">
              <input
                type="checkbox"
                checked={autoRoles.includes(r)}
                onChange={(e) => setAutoRoles((cur) => (
                  e.target.checked ? [...cur, r] : cur.filter((x) => x !== r)
                ))}
              />
              {r}
            </label>
          ))}
        </div>
      </div>

      <h3 className="small" style={{ marginTop: '1rem' }}>Conditions</h3>
      <CheckField
        label="Only while their membership is active"
        checked={requiresMembership}
        onChange={setRequiresMembership}
        hint="Checked at every sync, so access lapses on its own."
      />
      <CheckField
        label="Only while their waivers are signed"
        checked={requiresWaivers}
        onChange={setRequiresWaivers}
      />

      <h3 className="small" style={{ marginTop: '1rem' }}>Doors it opens</h3>
      {doors.length === 0 && <p className="small">No doors have been added yet.</p>}
      {doors.map((d) => (
        <label key={d.id} className="check">
          <input
            type="checkbox"
            checked={doorIds.includes(d.id)}
            onChange={(e) => setDoorIds((cur) => (
              e.target.checked ? [...cur, d.id] : cur.filter((x) => x !== d.id)
            ))}
          />
          {d.name}
        </label>
      ))}
    </Modal>
  );
}

/* ---------------------------------------------------------------- doors -- */

function Doors() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [providerRef, setProviderRef] = useState('');
  const [remote, setRemote] = useState(false);

  const doors = useQuery({
    queryKey: ['access', 'doors'],
    queryFn: () => api.get<{ data: AccessDoor[] }>('/access/doors').then((r) => r.data),
  });

  // What the provider says it has, so a door is picked from a list rather than
  // an id being copied out of a vendor console by hand.
  const fromProvider = useQuery({
    queryKey: ['access', 'doors', 'provider'],
    queryFn: () => api.get<{ data: Array<{ ref: string; name: string }> }>(
      '/access/doors/provider',
    ).then((r) => r.data),
    retry: false,
  });

  const create = useMutation({
    mutationFn: () => api.post('/access/doors', {
      name,
      slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
      providerRef: providerRef || null,
      allowsRemoteUnlock: remote,
    }),
    onSuccess: () => {
      toast.success('Door added.');
      setAdding(false); setName(''); setProviderRef(''); setRemote(false);
      void queryClient.invalidateQueries({ queryKey: ['access'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const unlock = useMutation({
    mutationFn: (id: string) => api.post(`/access/doors/${id}/unlock`, {
      reason: 'Opened from Club Office',
    }),
    onSuccess: () => toast.success('Door opened.'),
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (doors.isLoading) return <Loading what="Reading the doors" />;
  if (doors.error) return <ErrorNote error={doors.error} />;

  return (
    <>
      <Card
        title="Doors"
        actions={<button className="btn btn--sm" type="button" onClick={() => setAdding(true)}>
          Add a door
        </button>}
        flush
      >
        {doors.data?.length
          ? (
            <table className="table">
              <thead>
                <tr><th>Door</th><th>Linked to</th><th /></tr>
              </thead>
              <tbody>
                {doors.data.map((d) => (
                  <tr key={d.id}>
                    <td>{d.name} {!d.isActive && <Pill tone="bad">off</Pill>}</td>
                    <td className="mono small">{d.providerRef ?? 'not linked'}</td>
                    <td>
                      {d.allowsRemoteUnlock && d.providerRef && (
                        <button
                          className="btn btn--sm"
                          type="button"
                          disabled={unlock.isPending}
                          onClick={() => {
                            if (window.confirm(`Open ${d.name} now? This is logged.`)) {
                              unlock.mutate(d.id);
                            }
                          }}
                        >
                          Open now
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
          : <div className="card__body"><Empty title="No doors yet" /></div>}
      </Card>

      {adding && (
        <Modal
          title="Add a door"
          onClose={() => setAdding(false)}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setAdding(false)}>Cancel</button>
              <button
                className="btn btn--primary"
                type="button"
                disabled={!name.trim() || create.isPending}
                onClick={() => create.mutate()}
              >
                Add
              </button>
            </>
          }
        >
          <TextField label="Name" value={name} onChange={setName} placeholder="Front door" />
          {fromProvider.data && fromProvider.data.length > 0
            ? (
              <SelectField
                label="Which door at the provider"
                value={providerRef}
                onChange={setProviderRef}
                options={[
                  { value: '', label: 'Not linked' },
                  ...fromProvider.data.map((d) => ({ value: d.ref, label: d.name })),
                ]}
              />
            )
            : (
              <TextField
                label="The provider's door id"
                value={providerRef}
                onChange={setProviderRef}
                hint="Leave blank for a door the club opens with a key."
              />
            )}
          <CheckField
            label="May be opened from the office"
            checked={remote}
            onChange={setRemote}
            hint="Leave off for anything but a main entrance."
          />
        </Modal>
      )}
    </>
  );
}

/* --------------------------------------------------------------- events -- */

function Events() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [decision, setDecision] = useState<'' | 'granted' | 'denied'>('');

  const events = useQuery({
    queryKey: ['access', 'events', decision],
    queryFn: () => api.get<{ data: AccessEvent[] }>(
      `/access/events?perPage=100${decision ? `&decision=${decision}` : ''}`,
    ).then((r) => r.data),
    refetchInterval: 60_000,
  });

  const reconcile = useMutation({
    mutationFn: () => api.post<{ data: { fetched: number; stored: number } }>('/access/reconcile')
      .then((r) => r.data),
    onSuccess: (r) => {
      toast.success(`Fetched ${r.fetched}, ${r.stored} new.`);
      void queryClient.invalidateQueries({ queryKey: ['access', 'events'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card
      title="Door log"
      actions={
        <>
          <SelectField
            label=""
            value={decision}
            onChange={setDecision}
            options={[
              { value: '', label: 'Everything' },
              { value: 'granted', label: 'Opened' },
              { value: 'denied', label: 'Refused' },
            ] as const}
          />
          <button
            className="btn btn--sm"
            type="button"
            disabled={reconcile.isPending}
            onClick={() => reconcile.mutate()}
          >
            Fetch missed
          </button>
        </>
      }
      flush
    >
      {events.isLoading && <div className="card__body"><Loading /></div>}
      {events.error && <div className="card__body"><ErrorNote error={events.error} /></div>}
      {events.data?.length === 0 && (
        <div className="card__body">
          <Empty title="No door events yet">
            Events arrive by webhook as they happen, or with &ldquo;Fetch missed&rdquo;.
          </Empty>
        </div>
      )}
      {events.data && events.data.length > 0 && (
        <table className="table">
          <thead>
            <tr><th>When</th><th>Door</th><th>Who</th><th>What</th></tr>
          </thead>
          <tbody>
            {events.data.map((e) => (
              <tr key={e.id}>
                <td className="mono small">{new Date(e.occurredAt).toLocaleString()}</td>
                <td>{e.doorName ?? '—'}</td>
                <td>
                  {e.memberName ?? e.actorName ?? <em>unidentified</em>}
                  {e.credentialLabel && <span className="small"> · {e.credentialLabel}</span>}
                </td>
                <td>
                  <Pill tone={e.decision === 'granted' ? 'good' : e.decision === 'denied' ? 'bad' : 'quiet'}>
                    {e.decision}
                  </Pill>
                  {e.reason && <span className="small"> {e.reason}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
